import { getConfig } from '@leagueplatform/config';
import { Auth } from '@leagueplatform/auth';

const SALESFORCE_CHAT_TOKEN_PATH = '/v1/salesforce-chat-token';

interface SalesforceChatTokenResponse {
  data: {
    attributes: {
      token: string;
      expiresIn: number;
    };
  };
}

/**
 * Fetches a limited-scope Salesforce chat identity token from the League Identity API.
 * Returns a short-lived JWT (30 min) intended for use with Salesforce Enhanced Web Chat.
 *
 * Exchanges the current user's League session (via `Auth.getToken()`) for a
 * Salesforce-specific JWT. This limited-scope token should be used in place of the
 * full League JWT when communicating with Salesforce.
 */
export const getSalesforceChatToken = async (): Promise<string> => {
  const {
    core: {
      api: { url: apiUrl },
    },
  } = getConfig().config;

  const bearerToken = await Auth.getToken();

  const response = await fetch(`${apiUrl}${SALESFORCE_CHAT_TOKEN_PATH}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/vnd.api+json',
      Authorization: `Bearer ${bearerToken}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Salesforce chat token request failed: ${response.status}`);
  }

  const data = (await response.json()) as SalesforceChatTokenResponse;
  const token = data.data?.attributes?.token;

  if (!token) {
    throw new Error('Salesforce chat token response missing token.');
  }

  return token;
};
